import { BrowserModule } from "@angular/platform-browser";
import { NgModule, ErrorHandler, Injectable } from "@angular/core";
import { HttpErrorResponse, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from "@angular/common/http";

import { environment } from "../../environments/environment";

import * as Sentry from "@sentry/angular";
import { Observable, observable } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';

Sentry.init({
    dsn: "https://1ca5ebf89b2d4e0baa07ad83ba0d2649@o395515.ingest.sentry.io/5247673",
    integrations: [
        Sentry.replayIntegration(),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: environment.sentry_environment,
    release: environment.sentry_release,
    beforeSend(event) {
        return environment.production == true ? event : null;
    },
    debug: false
});

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
    constructor() { }

    private lastError: string;

    extractError(error) {
        // Try to unwrap zone.js error.
        // https://github.com/angular/angular/blob/master/packages/core/src/util/errors.ts
        if (error && error.ngOriginalError) {
            error = error.ngOriginalError;
        }

        // We can handle messages and Error objects directly.
        if (typeof error === "string" || error instanceof Error) {
            return error;
        }

        // If it's http module error, extract as much information from it as we can.
        if (error instanceof HttpErrorResponse) {
            // The `error` property of http exception can be either an `Error` object, which we can use directly...
            if (error.error instanceof Error) {
                return error.error;
            }

            // ... or an`ErrorEvent`, which can provide us with the message but no stack...
            if (error.error instanceof ErrorEvent) {
                return error.error.message;
            }

            // ...or the request body itself, which we can use as a message instead.
            if (typeof error.error === "string") {
                return `Server returned code ${error.status} with body "${error.error}"`;
            }

            // If we don't have any detailed information, fallback to the request message itself.
            return error.message;
        }

        // Skip if there's no error, and let user decide what to do with it.
        return null;
    }

    handleError(error) {
        let extractedError = this.extractError(error) || error.stack;

        if (environment.sentry_enabled) {
            const errorString = error.toString();
            if (errorString === this.lastError) {
                return;
            }

            const eventId = Sentry.captureException(extractedError);

            if (!environment.production) {
                console.error(extractedError);
            } else {
                // Bypass filemedia url popup
                if (error?.url?.includes('file-medias') && error?.url?.includes('url')) {
                    return;
                }

                // Optionally show user dialog to provide details on what happened.
                // Sentry.showReportDialog({ eventId });
            }

            this.lastError = errorString;
        } else {
            console.error(extractedError);
        }
    }
}

@Injectable()
export class SentryHttpInterceptor implements HttpInterceptor {
    constructor(protected keycloakAngular: KeycloakService) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const transactionId = Math.random().toString(36).substr(2, 9);
        Sentry.setTag('transaction_id', transactionId);

        const authReq = req.clone({ //just an authorization test
            setHeaders: { "x-transaction-id": transactionId }
        });

        return next.handle(authReq);
    }
}